// Import styles
import './front.scss';

// Import loading and popup functionality from theme
import { isLoading } from '../../../../themes/totaldigital/assets/scripts/common/loader.js';
import popupConfirm from '../../../../themes/totaldigital/assets/scripts/common/popupConfirm.js';

/** 
 * TD Pardot Forms Frontend JavaScript
 * Handles form validation, submission, and user interaction for Pardot forms
 * Includes functionality migrated from old salesforce-ninja.js and pardot-forms.js
 */
document.addEventListener('DOMContentLoaded', () => {
    initPardotForms();
    initHandlerActionsForms();
    initLegacyPardotForms();
});

/**
 * Initialize Pardot forms on the page
 * Sets up validation and submission handlers for all forms
 */
const initPardotForms = () => {
    const forms = document.querySelectorAll('.sf-forms');
    
    forms.forEach(form => {
        initFormValidation(form);
        initFormSubmission(form);
    });
};

/**
 * Initialize handler-actions forms (migrated from salesforce-ninja.js)
 * Handles forms within .handler-actions containers for special processing
 */
const initHandlerActionsForms = () => {
    // Check if we're on a page with handler-actions forms
    const handlerActionsForms = document.querySelectorAll('.handler-actions .sf-forms');
    
    if (handlerActionsForms.length > 0) {
        // Add special data attribute to identify handler-actions forms
        handlerActionsForms.forEach(form => {
            form.dataset.handlerActions = 'true';
        });
    }
};

/**
 * Initialize legacy Pardot forms (migrated from pardot-forms.js)
 * Handles forms with .pardot-form class for backward compatibility
 */
const initLegacyPardotForms = () => {
    const pardotForms = document.querySelectorAll(".pardot-form");

    pardotForms.forEach(function(form) {
        const formHandlerUrl = form.getAttribute("action");

        if (!formHandlerUrl) {
            return;
        }

        form.addEventListener("submit", function(e) {
            e.preventDefault();

            const requiredFields = form.querySelectorAll("[required]");
            let isValid = true;

            requiredFields.forEach(function(field) {
                if (!field.value.trim()) {
                    isValid = false;
                    field.classList.add("is-invalid");
                } else {
                    field.classList.remove("is-invalid");
                }
            });

            if (!isValid) {
                return false;
            }

            const formData = new FormData(form);
            const params = new URLSearchParams();

            params.append('action', 'pardot_form_proxy');
            params.append('form_handler_url', formHandlerUrl);

            for (const [key, value] of formData.entries()) {
                params.append(key, value);
            }

            fetch(fintua.ajaxurl, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: params.toString()
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Network response was not ok");
                    }
                    return response.json();
                })
                .then(data => {
                    if (data.success && data.data.message === "success") {
                        form.innerHTML = '<div class="alert alert-success">Thank you for your submission!</div>';
                    } else {
                        form.innerHTML = '<div class="alert alert-danger">There was an error processing your submission. Please try again later.</div>';
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                    form.innerHTML = '<div class="alert alert-danger">There was an error processing your submission. Please try again later.</div>';
                });
        });
    });
};

/**
 * Initialize form validation for a specific form
 * @param {HTMLFormElement} form - Form element to initialize
 */
const initFormValidation = (form) => {
    const emailFields = form.querySelectorAll('.sf__input--email');
    
    // Business email validation: as-you-type and on blur, using excludedDomains from server
    emailFields.forEach(field => {
        field.addEventListener('input', () => validateBusinessEmail(field));
        field.addEventListener('blur', () => validateBusinessEmail(field));
    });
    
    // Phone fields: strip non-digit/+ characters on input
    const phoneFields = form.querySelectorAll('.sf__input--tel');
    phoneFields.forEach(field => {
        field.addEventListener('input', () => {
            const cursor = field.selectionStart;
            const before = field.value;
            field.value = before.replace(/[^0-9+]/g, '').replace(/(?!^)\+/g, '');
            const diff = before.length - field.value.length;
            field.setSelectionRange(cursor - diff, cursor - diff);
        });
        field.addEventListener('paste', (e) => {
            e.preventDefault();
            const paste = (e.clipboardData || window.clipboardData).getData('text');
            const cleaned = paste.replace(/[^0-9+]/g, '').replace(/(?!^)\+/g, '');
            document.execCommand('insertText', false, cleaned);
        });
    });

    // Real-time validation for all fields. Each interaction also marks the form
    // as "touched" so the Submit button starts honouring the validation gate.
    const inputs = form.querySelectorAll('.sf__input');
    inputs.forEach(input => {
        input.addEventListener('input', () => { markFormInteracted(form); validateField(input); });
        input.addEventListener('blur',  () => { markFormInteracted(form); validateField(input); });
        input.addEventListener('change',() => { markFormInteracted(form); validateField(input); });
    });

    // Radio button validation (for radio groups)
    const radioGroups = form.querySelectorAll('.sf__radio-group');
    radioGroups.forEach(group => {
        const radios = group.querySelectorAll('.sf__input--radio');
        radios.forEach(radio => {
            radio.addEventListener('change', () => { markFormInteracted(form); validateRadioGroup(group); });
        });
    });

    // Form submission validation. Even if the user clicks Submit without
    // touching any field, this counts as an interaction — from that point on
    // the Submit button reflects validation state.
    form.addEventListener('submit', (e) => {
        e.preventDefault();
        markFormInteracted(form);
        if (!validateForm(form)) {
            return;
        }
        // Fields are valid — but if Turnstile is required AND we don't have a
        // verified session OR a fresh token, surface the verification overlay
        // instead of submitting (the same overlay used for server-side
        // verification_required errors). The "Verify again" button there clears
        // any stale cookie and re-opens the footer challenge.
        const needsVerification = !tdPardotForms.turnstileDisabled
            && !isAlreadyVerified()
            && !window.turnstileToken;
        if (needsVerification) {
            // "Pending" mode — the user hasn't tried to verify yet, this is just
            // a reminder that they need to. Not the alarming failure variant.
            showVerificationOverlay(form, { mode: 'pending' });
            return;
        }
        submitForm(form);
    });

    // Defensive cleanup: an earlier build appended a .sf__submit-hint div for
    // disabled-state feedback. The hint is gone — yank any leftover DOM so
    // cached HTML doesn't leave a phantom error notice under the button.
    const staleHint = form.querySelector('.sf__submit-hint');
    if (staleHint) staleHint.remove();

    // Initial state. The form hasn't been interacted with yet, so the button
    // stays enabled regardless of empty required fields. The first interaction
    // (above) flips the gate on.
    updateSubmitButtonState(form);

    // Page-load verification gate: if Turnstile is required and the visitor's
    // session isn't already verified, show the overlay BEFORE they start filling
    // the form — solving the security check first is much less annoying than
    // discovering it after typing everything in. The overlay can only be closed
    // by a successful grant (hideAllVerificationOverlays in grantSessionVerification).
    if (!tdPardotForms.turnstileDisabled && !isAlreadyVerified()) {
        showVerificationOverlay(form, { mode: 'pending' });
    }
};

/**
 * Validate business email address
 * @param {HTMLInputElement} emailField - Email input field
 * @returns {boolean} - Whether email is valid
 */
const validateBusinessEmail = (emailField) => {
    const email = emailField.value.trim();

    clearFieldError(emailField);

    if (!email) {
        return true;
    }

    if (!isValidEmail(email)) {
        showFieldError(emailField, tdPardotForms.strings.invalidEmail);
        return false;
    }

    const isBusiness = isBusinessEmail(email);
    if (!isBusiness) {
        showFieldError(emailField, tdPardotForms.strings.businessEmailError);
        return false;
    }

    return true;
};

/**
 * Check if email format is valid
 * @param {string} email - Email address to validate
 * @returns {boolean} - Whether email format is valid
 */
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Check if email is a business email (not from common personal providers)
 * @param {string} email - Email address to check
 * @returns {boolean} - Whether email is a business email
 */
const isBusinessEmail = (email) => {
    const defaultExcluded = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
        'icloud.com', 'mail.com', 'protonmail.com', 'tutanota.com',
        'test.com', 'example.com'
    ];
    const excludedDomains = (typeof tdPardotForms !== 'undefined' && Array.isArray(tdPardotForms.excludedDomains) && tdPardotForms.excludedDomains.length > 0)
        ? tdPardotForms.excludedDomains
        : defaultExcluded;

    const emailLower = email.toLowerCase();

    for (const domain of excludedDomains) {
        const d = (domain && typeof domain === 'string') ? domain.trim().toLowerCase() : '';
        if (d && emailLower.endsWith('@' + d)) {
            return false;
        }
    }

    return true;
};

/**
 * Validate individual field
 * @param {HTMLInputElement} field - Field element to validate
 * @returns {boolean} - Whether field is valid
 */
const validateField = (field) => {
    // For email fields, delegate entirely to the business email validator
    if (field.type === 'email') {
        return validateBusinessEmail(field);
    }

    clearFieldError(field);

    const customErrorMessage = field.dataset.errorMessage || tdPardotForms.strings.requiredField;

    if (field.hasAttribute('required')) {
        if (field.type === 'checkbox' && !field.checked) {
            showFieldError(field, customErrorMessage);
            return false;
        } else if (field.type !== 'checkbox' && !field.value.trim()) {
            showFieldError(field, customErrorMessage);
            return false;
        }
    }

    // Phone validation: digits and optional leading +
    if (field.type === 'tel' && field.value.trim()) {
        if (!/^\+?[0-9]+$/.test(field.value.trim())) {
            showFieldError(field, 'Please enter a valid phone number (digits only).');
            return false;
        }
    }

    // Number validation
    if (field.type === 'number' && field.value.trim()) {
        if (isNaN(field.value) || field.value === '') {
            showFieldError(field, 'Please enter a valid number.');
            return false;
        }
    }
    
    // Date validation
    if (field.type === 'date' && field.value.trim()) {
        const date = new Date(field.value);
        if (isNaN(date.getTime())) {
            showFieldError(field, 'Please enter a valid date.');
            return false;
        }
    }
    
    return true;
};

/**
 * Validate radio button group
 * @param {HTMLElement} group - Radio group container
 * @returns {boolean} - Whether radio group is valid
 */
const validateRadioGroup = (group) => {
    const radios = group.querySelectorAll('.sf__input--radio');
    const isRequired = group.querySelector('.sf__input--radio[required]') !== null;
    const customErrorMessage = group.dataset.errorMessage || 'Please select an option.';
    
    if (isRequired) {
        const checked = group.querySelector('.sf__input--radio:checked');
        if (!checked) {
            // Find the first radio button to show error on
            const firstRadio = radios[0];
            if (firstRadio) {
                showFieldError(firstRadio, customErrorMessage);
            }
            return false;
        }
    }
    
    // Clear error if valid
    radios.forEach(radio => {
        clearFieldError(radio);
    });
    
    // Update submit button state
    const form = group.closest('form');
    if (form) {
        updateSubmitButtonState(form);
    }
    
    return true;
};

/**
 * Validate entire form
 * @param {HTMLFormElement} form - Form element to validate
 * @returns {boolean} - Whether form is valid
 */
const validateForm = (form) => {
    const fields = form.querySelectorAll('.sf__input');
    const radioGroups = form.querySelectorAll('.sf__radio-group');
    let isValid = true;
    
    // Validate individual fields
    fields.forEach(field => {
        if (!validateField(field)) {
            isValid = false;
        }
    });
    
    // Validate radio groups
    radioGroups.forEach(group => {
        if (!validateRadioGroup(group)) {
            isValid = false;
        }
    });

    // Turnstile is checked separately in the submit handler — failures surface
    // via the verification overlay (not as an inline error here).

    return isValid;
};

/**
 * Show field error message
 * @param {HTMLInputElement} field - Field element
 * @param {string} message - Error message to display
 */
const showFieldError = (field, message) => {
    const errorElement = document.getElementById(field.id + '-error');
    if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.setProperty('display', 'block', 'important');
    }

    field.classList.add('sf__error');
    field.setAttribute('aria-invalid', 'true');
    
    // Mark validation as triggered when first error is shown
    const form = field.closest('form');
    if (form) {
        form.dataset.validationTriggered = 'true';
        updateSubmitButtonState(form);
    }
};

/**
 * Clear field error message
 * @param {HTMLInputElement} field - Field element
 */
const clearFieldError = (field) => {
    const errorElement = document.getElementById(field.id + '-error');
    if (errorElement) {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
    }
    
    field.classList.remove('sf__error');
    field.setAttribute('aria-invalid', 'false');
    
    // Update submit button state when error is cleared
    const form = field.closest('form');
    if (form) {
        updateSubmitButtonState(form);
    }
};

/**
 * Show Turnstile error message
 */
const showTurnstileError = () => {
    const errorElement = document.getElementById('turnstile-error');
    if (errorElement) {
        errorElement.textContent = tdPardotForms.strings.turnstileError;
        errorElement.style.display = 'block';
    }
    
    // Update submit button state when Turnstile error is shown
    const form = errorElement?.closest('form');
    if (form) {
        updateSubmitButtonState(form);
    }
};

/**
 * Show error popup (migrated from salesforce-ninja.js)
 */
const showErrorPopup = () => {
    popupConfirm(
        "Submission Error",
        "There was an error processing your submission.",
        "Please try again or contact support if the problem persists.",
        "Try Again",
        "Contact us"
    ).then((confirmed) => {
        if (!confirmed) {
            window.location.href = '/contact/';
        }
    });
};

/**
 * Initialize form submission for a specific form
 * @param {HTMLFormElement} form - Form element to initialize
 */
const initFormSubmission = (form) => {
    const submitBtn = form.querySelector('.sf__button');
    if (submitBtn) {
        // Store original button text
        submitBtn.dataset.originalText = submitBtn.textContent;
    }
};

/**
 * Submit form via AJAX
 * @param {HTMLFormElement} form - Form element to submit
 */
const submitForm = async (form) => {
    const submitBtn = form.querySelector('.sf__button');
    if (!submitBtn) return;
    
    const originalText = submitBtn.dataset.originalText;
    
    try {
        // Disable submit button and show loading
        submitBtn.disabled = true;
        submitBtn.textContent = tdPardotForms.strings.submitting;
        
        // Prepare form data
        const formData = new FormData(form);
        formData.append('action', 'td_pardot_form_submit');
        formData.append('nonce', tdPardotForms.nonce);
        // Only send a Turnstile token when we actually have one (typically: we just
        // solved the challenge in this request lifecycle but the grant call hasn't
        // landed yet). When the session is already verified the server reads the
        // cookie instead — no token needed.
        if (!tdPardotForms.turnstileDisabled && window.turnstileToken) {
            formData.append('turnstile_token', window.turnstileToken);
        }
        
        // Submit form
        const response = await fetch(tdPardotForms.ajaxUrl, {
            method: 'POST',
            body: formData
        });
        
        const result = await response.json();
        const pardotStatus = result.data?.pardot_status || 'failed';
        const isBusinessSuccess = result.success && pardotStatus === 'success';

        if (isBusinessSuccess) {
            // Check if this is a handler-actions form
            const isHandlerActionsForm = form.dataset.handlerActions === 'true' || 
                                       form.closest('.handler-actions') !== null;
            
            // Set submission cookie if successful (for both regular and handler-actions forms)
            if (result.data?.cookie) {
                document.cookie = `fintua_submissions=${result.data.cookie};path=/;max-age=${365 * 24 * 60 * 60}`;
            }

            // Confirmed lead: the server accepted the submission and Pardot returned success.
            // Pushed only here — never on the submit gesture — so invalid, blocked or failed
            // attempts never reach GA4 / Google Ads.
            const leadPushed = pushGenerateLead(form, originalText);
            
            if (isHandlerActionsForm) {
                // Handle success submission for handler-actions forms
                await handleSuccessSubmission(form);
            } else {
                // Handle redirect URL or thank you message
                const redirectUrl = result.data?.redirect_url;
                const thankYouMessage = result.data?.thank_you_message;
                
                // Check if form is in an iframe (popup)
                const isInIframe = window.self !== window.top;
                const isInPopup = document.querySelector('.popup-form-content') !== null;
                
                if (redirectUrl && (isInIframe || isInPopup)) {
                    // Send message to parent page instead of redirecting in iframe
                    if (window.parent && window.parent !== window) {
                        window.parent.postMessage({
                            action: 'formSubmitted',
                            type: 'redirect',
                            url: redirectUrl,
                            message: thankYouMessage || 'Form submitted successfully'
                        }, window.location.origin);
                    }
                    // Show success message in iframe
                    showFormSuccess(form, thankYouMessage || 'Form submitted successfully');
                } else if (redirectUrl) {
                    // Redirect to the specified URL (normal page). Wait for GTM to dispatch
                    // the lead tags first, otherwise the navigation can cancel their hits.
                    await leadPushed;
                    window.location.href = redirectUrl;
                } else if (thankYouMessage) {
                    // Show custom thank you message
                    showFormSuccess(form, thankYouMessage);
                } else {
                    // Show standard success message
                    showFormSuccess(form);
                }
            }
        } else {
            // Server told us the visitor's verification cookie is missing/invalid.
            // Throw away the local "verified" state, show the retry overlay on this
            // form, and unhide the footer widget so the user can re-solve.
            if (result.data?.code === 'verification_required') {
                window.tdPardotSessionVerified = false;
                if (tdPardotForms) tdPardotForms.verifiedOnLoad = false;
                document.querySelectorAll('.sf__turnstile-wrapper').forEach(el => {
                    el.classList.remove('sf__turnstile-wrapper--hidden');
                });
                showVerificationOverlay(form, { mode: 'failed', message: result.data?.message });
            } else {
                const errorMessage = result.data?.error_message || result.data?.message || tdPardotForms.strings.error;
                showFormError(form, errorMessage);
            }
        }
        
    } catch (error) {
        console.error('td-pardot: Form submission error:', error);
        showFormError(form, tdPardotForms.strings.error);
    } finally {
        // Reset button
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
        
        // Reset Turnstile
        if (window.turnstile) {
            window.turnstile.reset();
        }
        window.turnstileToken = null;
        // Re-show the widget so the user can re-verify (only meaningful on error retries;
        // on success the form HTML is replaced so this is a no-op).
        document.querySelectorAll('.sf__turnstile-wrapper').forEach(el => {
            el.classList.remove('sf__turnstile-wrapper--hidden');
        });
    }
};

/**
 * Handle success submission for handler-actions forms (migrated from salesforce-ninja.js)
 * @param {HTMLFormElement} form - Form element that was submitted
 */
const handleSuccessSubmission = async (form) => {
    const container = document.querySelector(".resource__content");
    if (!container) {
        // Fallback to standard success message if no resource content
        showFormSuccess(form);
        return;
    }

    const template = container.dataset.template ?? "";
    const pageId = container.dataset.id ?? "";
    const pageTitle = document.title ?? "";
    const formId = form.dataset.formId ?? "";
    const formName = form.querySelector('.sf__button')?.dataset.originalText ?? "Form";
    
    if (!template || !pageId) {
        showFormSuccess(form);
        return;
    }

    try {
        // Show loading state
        isLoading(true);

        // Send GA4 event
        sendGa4Event(template, formId, formName, pageTitle, pageId);

        // Get submitted template part
        const detailBody = new URLSearchParams({
            action: "submitted_template_part",
            template,
            page_id: pageId,
        });

        // Add cookie value to request body for authorization
        const fintuaSubmissionsCookie = document.cookie
            .split('; ')
            .find(row => row.startsWith('fintua_submissions='));
        if (fintuaSubmissionsCookie) {
            const cookieValue = fintuaSubmissionsCookie.split('=')[1];
            detailBody.append('fintua_submissions_cookie', cookieValue);
        }

        const response = await fetch(tdPardotForms.ajaxUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            },
            body: detailBody,
        });

        const result = await response.json();
        
        if (!result.success) {
            throw new Error(result.data?.message || "Unknown error");
        }
        
        // Replace container content
        container.innerHTML = result.data.html;
        window.scrollTo({top: 0, left: 0, behavior: "smooth"});
        
    } catch (error) {
        console.error('Success submission error:', error);
        showFormError(form, 'There was an error processing your submission. Please try again later.');
    } finally {
        // Hide loading state
        isLoading(false);
    }
};

/**
 * Push a confirmed-lead event for GTM. The payload keys match the existing GTM
 * variables: DLV - generate lead form - Id (formId), DLV - formSource URL -
 * Generate_lead (url) and DLV - generate lead form - Label (label).
 * @param {HTMLFormElement} form - Form element that was submitted
 * @param {string} label - Human-readable form label (the submit button text)
 * @returns {Promise<void>} Resolves once GTM has run the tags, or after 1s at most
 */
const pushGenerateLead = async (form, label) => {
    const userData = await collectHashedUserData(form);

    return new Promise((resolve) => {
        window.dataLayer = window.dataLayer || [];
        // Resolve on whichever comes first: GTM's callback, or the timeout when GTM is
        // missing or blocked (the callback then never fires).
        const timer = setTimeout(resolve, 1000);
        window.dataLayer.push({
            event: 'generate_lead',
            formId: form.dataset.formId ?? '',
            url: window.location.href,
            label: (label ?? '').trim(),
            user_data: userData,
            eventCallback: () => {
                clearTimeout(timer);
                resolve();
            },
            eventTimeout: 1000,
        });
    });
};

/**
 * SHA-256 a string and return it as lowercase hex, the format Google expects for
 * enhanced conversions. Returns null when the browser has no Web Crypto (non-HTTPS).
 * @param {string} value
 * @returns {Promise<string|null>}
 */
const sha256Hex = async (value) => {
    if (!window.crypto?.subtle) return null;
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Read the fields Google Ads enhanced conversions can use and hash the personal ones
 * before they leave the form. Fields are found by their Pardot handler name, falling
 * back to the autocomplete attribute: data-prospect-api-field-id is rendered empty for
 * everything except the GDPR checkbox, so it cannot be used to locate them.
 *
 * Every value is read synchronously, before the first await. showFormSuccess replaces
 * the form with `form.innerHTML = ...` as soon as this function yields, so anything
 * read after an await comes back empty.
 *
 * Only hashes reach the dataLayer — plain addresses and names never do, so the other
 * tags in the container (pixels, custom HTML) cannot read a visitor's details.
 *
 * @param {HTMLFormElement} form
 * @returns {Promise<Object|undefined>} undefined when there is no email to send
 */
const collectHashedUserData = async (form) => {
    const readField = (...selectors) => {
        for (const selector of selectors) {
            const el = form.querySelector(selector);
            if (el?.value?.trim()) return el.value.trim();
        }
        return '';
    };

    // Snapshot the whole form first — see the note above about showFormSuccess.
    const raw = {
        // Google normalises email as trimmed + lowercase before hashing.
        email: readField('[name="email"]', 'input[type="email"]', '[autocomplete="email"]').toLowerCase(),
        firstName: readField('[name="first_name"]', '[autocomplete="given-name"]').toLowerCase(),
        lastName: readField('[name="last_name"]', '[autocomplete="family-name"]').toLowerCase(),
        country: readField('[name="country"]', '[autocomplete="country"]', '[autocomplete="country-name"]'),
        postalCode: readField('[name="zip"]', '[name="postal_code"]', '[autocomplete="postal-code"]'),
    };

    if (!raw.email) return undefined;

    const emailHash = await sha256Hex(raw.email);
    if (!emailHash) return undefined;

    const userData = { sha256_email_address: emailHash };
    const address = {};

    if (raw.firstName) address.sha256_first_name = await sha256Hex(raw.firstName);
    if (raw.lastName) address.sha256_last_name = await sha256Hex(raw.lastName);
    // Country and postcode are not personal on their own, so Google takes them as-is.
    if (raw.country) address.country = raw.country;
    if (raw.postalCode) address.postal_code = raw.postalCode;

    if (Object.keys(address).length) userData.address = address;

    return userData;
};


/**
 * Send GA4 event (migrated from salesforce-ninja.js)
 * @param {string} templateName - Template name
 * @param {string} formId - Form ID
 * @param {string} formName - Form name
 * @param {string} pageTitle - Page title
 * @param {string} pageId - Page ID
 */
const sendGa4Event = (templateName, formId, formName, pageTitle, pageId) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "resource_form_submission",
        resource_type: templateName,
        form_id: formId,
        form_name: formName,
        page_title: pageTitle,
        page_id: pageId,
        timestamp: new Date().toISOString(),
    });

    if (["fintuacom.local", "127.0.0.1"].includes(window.location.hostname)) {
        console.log("GA4 Event Sent:", {
            event: "resource_form_submission",
            resource_type: templateName,
            form_id: formId,
            form_name: formName,
            page_title: pageTitle,
            page_id: pageId,
        });
    }
};

/**
 * Show form success message
 * @param {HTMLFormElement} form - Form element
 * @param {string} customMessage - Optional custom success message
 */
const showFormSuccess = (form, customMessage = null) => {
    const message = customMessage || tdPardotForms.strings.success;
    form.innerHTML = `
        <div class="sf__success">
            ${message}
        </div>
    `;
    
    // Trigger custom events for form success
    const formId = form.dataset.formId;
    const formData = {
        formId: formId,
        message: message,
        form: form
    };
    
    // Trigger sf-form-success event (for backward compatibility)
    // Use vanilla JavaScript instead of jQuery
    const sfEvent = new CustomEvent('sf-form-success', {
        detail: formId,
        bubbles: true
    });
    document.dispatchEvent(sfEvent);

    // Trigger td-pardot-form-success event with more data
    const tdEvent = new CustomEvent('td-pardot-form-success', {
        detail: formData,
        bubbles: true
    });
    document.dispatchEvent(tdEvent);

    // Also trigger a custom event that can be listened for
    document.dispatchEvent(new CustomEvent('td-pardot-form-success', {
        detail: formData
    }));
};

/**
 * Show form error message
 * @param {HTMLFormElement} form - Form element
 * @param {string} message - Error message
 */
const showFormError = (form, message) => {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'sf__form-error';
    errorDiv.innerHTML = `
        <p>${message}</p>
    `;
    
    // Insert error at the top of the form
    form.insertBefore(errorDiv, form.firstChild);
    
    // Scroll to error
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Remove error after 5 seconds
    setTimeout(() => {
        if (errorDiv.parentNode) {
            errorDiv.remove();
        }
    }, 5000);
};

/**
 * Show the verification overlay on a form. Two modes:
 *
 *   - 'pending' (default for page-load): the visitor simply hasn't completed
 *     Turnstile yet. Spinner icon, calm copy, button labelled "Open security
 *     check". Footer widget gets scrolled into view so the user can solve it.
 *
 *   - 'failed':  server returned `verification_required`, or a Turnstile
 *     error/expired callback fired. Alarm icon, scary copy, "Verify again"
 *     button that revokes any stale cookie before re-prompting.
 *
 * @param {HTMLFormElement|null} form
 * @param {Object} [opts]
 * @param {'pending'|'failed'} [opts.mode='failed']
 * @param {string} [opts.title]   - override title
 * @param {string} [opts.message] - override body text
 */
const showVerificationOverlay = (form, opts = {}) => {
    const mode = opts.mode === 'pending' ? 'pending' : 'failed';
    const target = form || document.querySelector('form.sf-forms');
    if (!target) {
        // No form on the page (defensive — overlay needs a host). Just unhide the widget.
        document.querySelectorAll('.sf__turnstile-wrapper').forEach(el => {
            el.classList.remove('sf__turnstile-wrapper--hidden');
        });
        return;
    }

    let overlay = target.querySelector('.sf__verify-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'sf__verify-overlay';
        overlay.innerHTML = `
            <div class="sf__verify-overlay-icon" data-role="icon" aria-hidden="true"></div>
            <div class="sf__verify-overlay-title"></div>
            <div class="sf__verify-overlay-message"></div>
            <div class="sf__verify-overlay-widget" data-role="widget"></div>
            <button type="button" class="sf__verify-overlay-btn"></button>
        `;
        target.appendChild(overlay);
        overlay.querySelector('.sf__verify-overlay-btn').addEventListener('click', () => {
            retryVerification(target, overlay);
        });
    }

    overlay.classList.toggle('sf__verify-overlay--pending', mode === 'pending');
    overlay.classList.toggle('sf__verify-overlay--failed',  mode === 'failed');

    const iconEl  = overlay.querySelector('[data-role="icon"]');
    const titleEl = overlay.querySelector('.sf__verify-overlay-title');
    const msgEl   = overlay.querySelector('.sf__verify-overlay-message');
    const btnEl   = overlay.querySelector('.sf__verify-overlay-btn');

    if (mode === 'pending') {
        // Spinner stays visible until Turnstile mounts and renders its own UI.
        iconEl.innerHTML = '<span class="sf__verify-overlay-spinner" aria-hidden="true"></span>';
        titleEl.textContent = opts.title   || 'Security check';
        msgEl.textContent   = opts.message || 'Quickly verify you\'re human to continue.';
        btnEl.hidden = true;   // The widget below IS the action — no button needed.
    } else {
        iconEl.innerHTML = '⚠';
        titleEl.textContent = opts.title   || tdPardotForms.strings.verifyFailed || 'Security check failed.';
        msgEl.textContent   = opts.message || tdPardotForms.strings.verifyAgain  || '';
        btnEl.textContent   = tdPardotForms.strings.verifyRetry || 'Verify again';
        btnEl.hidden = false;
    }

    overlay.hidden = false;

    // Mount (or reset) the Turnstile widget inside the overlay. This is where the
    // user actually solves the challenge — no scrolling away.
    mountOverlayTurnstile(overlay);
};

/**
 * Ensure a Turnstile widget is rendered inside the given overlay's widget slot.
 * Waits for the Cloudflare api.js to be available, then calls turnstile.render()
 * with our session callback. Tracks the widget id on the overlay element so
 * retryVerification() can reset it in place rather than spawning duplicates.
 */
const mountOverlayTurnstile = (overlay) => {
    if (tdPardotForms.turnstileDisabled) return;
    const container = overlay.querySelector('[data-role="widget"]');
    if (!container) return;

    const sitekey = tdPardotForms.turnstileSiteKey;
    if (!sitekey) return;

    // If a widget already lives here, reset it instead of rendering a new one.
    if (overlay.dataset.turnstileWidgetId && window.turnstile) {
        try { window.turnstile.reset(overlay.dataset.turnstileWidgetId); } catch (e) {}
        return;
    }

    const doRender = () => {
        if (!window.turnstile) return false;
        // Clear any stale markup before mounting fresh.
        container.innerHTML = '';
        try {
            const id = window.turnstile.render(container, {
                sitekey: sitekey,
                callback: window.tdPardotSessionTurnstileCallback,
                'error-callback': window.tdPardotSessionTurnstileError,
                'expired-callback': window.tdPardotSessionTurnstileExpired,
                theme: 'light',
            });
            overlay.dataset.turnstileWidgetId = id;
            return true;
        } catch (e) {
            return false;
        }
    };

    if (doRender()) return;

    // Script not loaded yet — make sure it's loading, then poll for it.
    loadTurnstileScript();
    let tries = 0;
    const max = 100; // ~10s at 100ms
    const iv = setInterval(() => {
        tries++;
        if (doRender() || tries >= max) {
            clearInterval(iv);
        }
    }, 100);
};

/**
 * Hide any open verification overlay across all forms. Called when verification
 * actually succeeds (server-minted cookie via grantSessionVerification).
 */
const hideAllVerificationOverlays = () => {
    document.querySelectorAll('.sf__verify-overlay').forEach(o => { o.hidden = true; });
};

/**
 * "Verify again" handler. Tells the server to throw away the verified cookie,
 * unhides the footer widget, asks Cloudflare for a fresh challenge, and removes
 * the overlay so the user is back to a normal-but-disabled-button state.
 */
const retryVerification = async (form, overlay) => {
    // Ask server to drop the cookie so client + server agree we're not verified.
    try {
        const body = new URLSearchParams({
            action: 'td_pardot_revoke_verification',
            nonce: tdPardotForms.grantNonce,
        });
        await fetch(tdPardotForms.ajaxUrl, { method: 'POST', body, credentials: 'same-origin' });
    } catch (e) { /* best-effort */ }

    window.tdPardotSessionVerified = false;
    if (tdPardotForms) tdPardotForms.verifiedOnLoad = false;

    // Reset the Turnstile widget that lives inside THIS overlay (not the footer —
    // there is no footer widget any more). The overlay stays open so the user
    // can solve the new challenge in place.
    if (overlay && overlay.dataset.turnstileWidgetId && window.turnstile && typeof window.turnstile.reset === 'function') {
        try { window.turnstile.reset(overlay.dataset.turnstileWidgetId); } catch (e) {}
    }

    // Lazy-load the script if it wasn't loaded on this page yet.
    if (!tdPardotForms.turnstileDisabled) loadTurnstileScript();

    // Re-mount in case the widget was never created on this overlay for some reason.
    if (overlay) mountOverlayTurnstile(overlay);

    document.querySelectorAll('form.sf-forms').forEach(updateSubmitButtonState);
    // No scroll — the widget is right here, inside the overlay.
};

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

/**
 * Update submit button state based on form validation.
 *
 * The button is DEFAULT-DISABLED and only enables when:
 *   1. All [required] fields have valid values (text non-empty, checkbox checked,
 *      radio group has a selection, email is a valid business address, phone/number
 *      formatted correctly).
 *   2. Any non-required email that the user filled in is also a valid business email.
 *   3. Turnstile is either disabled in settings, or has produced a token via the
 *      tdPardotTurnstileCallback. This prevents submitting while the widget is still
 *      verifying.
 *
 * Errors aren't shown on page load (validateField is only triggered on user interaction),
 * but the button stays disabled visually so the user can't click through prematurely.
 */
const updateSubmitButtonState = (form) => {
    const submitBtn = form.querySelector('.sf__button');
    if (!submitBtn) return;

    const ready = isFormReadyToSubmit(form);
    // Only disable the button AFTER the user has interacted with the form.
    // A fresh, untouched form keeps an enabled CTA so it looks inviting; once
    // the user starts interacting we begin gating it on field validation state.
    // Turnstile is NOT part of this — that's surfaced via the verification
    // overlay at submit-click time, not by disabling the button.
    const interacted = form.dataset.interacted === 'true';
    submitBtn.disabled = interacted && !ready;

    if (submitBtn.disabled) {
        submitBtn.setAttribute('aria-disabled', 'true');
    } else {
        submitBtn.removeAttribute('aria-disabled');
    }
    // The hover-title is intentionally NOT set — the per-field inline error
    // messages already point to exactly which field needs attention.
    submitBtn.removeAttribute('title');
};

/**
 * Mark a form as "the user has started interacting with it". Once set, the
 * Submit button starts gating on validation state. Until then it stays
 * visually enabled regardless of empty required fields.
 */
const markFormInteracted = (form) => {
    if (form.dataset.interacted !== 'true') {
        form.dataset.interacted = 'true';
    }
    updateSubmitButtonState(form);
};

/**
 * Returns true if the form's FIELDS pass every gating rule for submission.
 * Turnstile/verification is intentionally NOT part of this check — that's handled
 * at submit-time via the verification overlay (the "Verify again" UX). Keeping
 * the field gating and the verification gating separate means a fresh visitor
 * with a partly-filled form sees an enabled CTA the moment they've filled out
 * everything we can validate locally, and the security challenge only surfaces
 * if/when verification actually needs to happen.
 */
const isFormReadyToSubmit = (form) => {
    const requiredFields = form.querySelectorAll('[required]');
    for (const field of requiredFields) {
        if (!isFieldValueValid(field)) return false;
    }
    // Non-required email fields: if the user typed something, it must still be a valid business email.
    const emailFields = form.querySelectorAll('input[type="email"]');
    for (const e of emailFields) {
        const v = (e.value || '').trim();
        if (!v) continue;
        if (!isValidEmail(v) || !isBusinessEmail(v)) return false;
    }
    return true;
};

/**
 * Returns true if a single field's value is acceptable for submission.
 * Used both for required field gating and the button-enable check.
 */
const isFieldValueValid = (field) => {
    if (field.disabled) return true; // skipped — not submitted
    if (field.type === 'checkbox') return field.checked;
    if (field.type === 'radio') {
        const name = field.name;
        if (!name) return field.checked;
        return !!(field.form && field.form.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`));
    }

    const v = (field.value || '').trim();
    if (!v) return false;

    if (field.type === 'email')  return isValidEmail(v) && isBusinessEmail(v);
    if (field.type === 'tel')    return /^\+?[0-9]+$/.test(v);
    if (field.type === 'number') return !isNaN(Number(v));
    return true;
};

/**
 * Whether the current visitor's session has already been Turnstile-verified
 * (cookie set by the server). When true, we skip the entire Turnstile flow.
 *
 * NB: wp_localize_script casts every scalar value to a STRING via
 * `(string) $value`, so the server-side boolean `true` arrives in JS as the
 * string "1" (and `false` as ""). We MUST use a truthy/falsy check here —
 * a strict `=== true` would always be false and the overlay would re-appear
 * on every page load even with a perfectly valid cookie.
 */
const isAlreadyVerified = () => {
    return !!(window.tdPardotSessionVerified || (tdPardotForms && tdPardotForms.verifiedOnLoad));
};

/**
 * POST a fresh Turnstile token to the server's grant endpoint. On success the
 * server mints the `turnstile_verified` cookie; we mirror that into runtime
 * state so subsequent submissions skip the token requirement.
 */
const grantSessionVerification = async (token) => {
    try {
        const body = new URLSearchParams({
            action: 'td_pardot_grant_verification',
            nonce: tdPardotForms.grantNonce,
            turnstile_token: token,
        });
        const res = await fetch(tdPardotForms.ajaxUrl, { method: 'POST', body, credentials: 'same-origin' });
        const data = await res.json();
        if (data && data.success && data.data && data.data.verified) {
            window.tdPardotSessionVerified = true;
            // Mirror so isAlreadyVerified picks it up regardless of which flag is checked.
            if (tdPardotForms) tdPardotForms.verifiedOnLoad = true;
            // Close any open verification overlays — verification has actually succeeded.
            // 2s grace so the user briefly sees Turnstile's own green check inside the
            // overlay before it fades out.
            setTimeout(() => hideAllVerificationOverlays(), 2000);
            // Refresh every form's Submit gating now that we're verified.
            document.querySelectorAll('form.sf-forms').forEach(form => updateSubmitButtonState(form));
            return true;
        }
    } catch (e) {
        // fall through to overlay
    }
    showVerificationOverlay();
    return false;
};

/**
 * Footer-widget success callback. We don't store the raw token on `window` any
 * more — it's single-use and Cloudflare will reject reuse. The cookie is the gate.
 */
window.tdPardotSessionTurnstileCallback = (token) => {
    grantSessionVerification(token);
};

/** Turnstile error/expired-callback handlers. Both surface the retry overlay. */
window.tdPardotSessionTurnstileError = () => { showVerificationOverlay(); };
window.tdPardotSessionTurnstileExpired = () => { showVerificationOverlay(); };

// Back-compat: any cached page HTML that still uses the old per-form callback name
// should not throw a ReferenceError if Turnstile resolves into it.
window.tdPardotTurnstileCallback = (token) => { grantSessionVerification(token); };

/**
 * Get visitor ID from cookie
 * @returns {string} - Visitor ID or empty string
 */
const getVisitorIdCookie = () => {
    const name = 'visitor_id822463=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i].trim();
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
};

/**
 * Load Turnstile script if not already loaded
 */
const loadTurnstileScript = () => {
    if (!document.querySelector('script[src*="turnstile"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }
};

/**
 * Add visitor ID to all Pardot forms
 */
const addVisitorIdToForms = () => {
    document.querySelectorAll('form.sf-forms').forEach(form => {
        let input = form.querySelector('input[name="visitor_id"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'visitor_id';
            form.appendChild(input);
        }
        input.value = getVisitorIdCookie();
        
        // Initialize submit button state
        updateSubmitButtonState(form);
    });
};

/**
 * Add current page URL to hidden url fields in all Pardot forms.
 * When form is in an iframe (e.g. popup modal), use parent page URL so Pardot gets the referring page with tracking params.
 */
const addCurrentUrlToForms = () => {
    let currentUrl = window.location.href;

    if (window.self !== window.top) {
        try {
            currentUrl = window.parent.location.href;
        } catch (e) {
            currentUrl = document.referrer || currentUrl;
        }
    }

    document.querySelectorAll('form.sf-forms').forEach(form => {
        const urlFields = form.querySelectorAll('input[name="url"][type="hidden"]');
        urlFields.forEach(field => {
            field.value = currentUrl;
        });
    });
};

// Load Cloudflare Turnstile only when we actually need to challenge the visitor.
// Skips both disabled-in-admin and already-verified-this-session — saves a network
// request on the majority of repeat page views in a single session.
if (!tdPardotForms.turnstileDisabled && !isAlreadyVerified()) {
    loadTurnstileScript();
}

// Add visitor ID and current URL to forms when DOM is ready
window.addEventListener('DOMContentLoaded', () => {
    addVisitorIdToForms();
    addCurrentUrlToForms();

    // (No footer Turnstile widget any more — the widget lives inside the
    // verification overlay, mounted on demand by mountOverlayTurnstile.)
});

// Handle .is-submitted button clicks (migrated from salesforce-ninja.js)
document.addEventListener('DOMContentLoaded', () => {
    document.querySelector(".is-submitted")?.addEventListener("click", () => {
        const container = document.querySelector(".resource__content");
        if (container) {
            const template = container.dataset.template ?? "";
            const pageId = container.dataset.id ?? "";
            const pageTitle = document.title ?? "";
            
            if (template && pageId) {
                sendGa4Event(template, "", "Submitted Button", pageTitle, pageId);
                
                const detailBody = new URLSearchParams({
                    action: "submitted_template_part",
                    template,
                    page_id: pageId,
                });

                // Show loading state
                isLoading(true);

                fetch(tdPardotForms.ajaxUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                    },
                    body: detailBody,
                })
                .then((res) => res.json())
                .then((json) => {
                    if (json.success) {
                        container.innerHTML = json.data.html;
                        window.scrollTo({top: 0, left: 0, behavior: "smooth"});
                    } else {
                        throw new Error(json.data?.message || "Unknown error");
                    }
                })
                .catch((error) => {
                    console.error('Error handling submitted button:', error);
                    showErrorPopup();
                })
                .finally(() => {
                    // Hide loading state
                    isLoading(false);
                });
            }
        }
    });
});
